import { useTranslation } from 'react-i18next';
import { SiteHeader } from '@/components/layout/SiteHeader';
import { SiteFooter } from '@/components/layout/SiteFooter';
import { useDocumentMeta } from '@/lib/useDocumentMeta';

interface Row {
  feature: string;
  keenez: string;
  smarthr: string;
  rippling: string;
  deel: string;
  bamboohr: string;
}

const ROWS: Row[] = [
  { feature: 'Public list price?', keenez: '✓ $0 / $8 / $19 / $39', smarthr: '✗ "Contact us"', rippling: '~ $8 base, opaque modules', deel: '~ partial', bamboohr: '✗ "Contact us"' },
  { feature: 'JP 社会保険 + 年末調整 + マイナンバー (native)', keenez: '✓ built', smarthr: '✓ built (their core)', rippling: '✗ none', deel: '~ payslip only', bamboohr: '✗ none' },
  { feature: 'JP payroll computation (not just distribution)', keenez: '✓ built', smarthr: '✗ delegates to freee/MFC', rippling: '✗ JP not supported', deel: '✓ via Deel Payroll', bamboohr: '✗ none' },
  { feature: 'US payroll (federal + state)', keenez: '✓ at v1 (federal + CA/NY/IL + no-tax states)', smarthr: '✗ none', rippling: '✓ best-in-class', deel: '✓ Managed Payroll', bamboohr: '~ TRAX add-on' },
  { feature: 'UK PAYE', keenez: '✓ at v1', smarthr: '✗ none', rippling: '~ limited', deel: '✓', bamboohr: '✗ partner' },
  { feature: 'DE Lohnsteuer', keenez: '✓ at v1', smarthr: '✗ none', rippling: '~ limited', deel: '✓', bamboohr: '✗ partner' },
  { feature: 'Cross-border compliance AI (Border-Buddy class)', keenez: '✓ flagship agent', smarthr: '✗ JP-only data model', rippling: '~ some agents', deel: '✓ Border Buddy', bamboohr: '✗' },
  { feature: 'Hanko / inkan approval flow', keenez: '✓ built-in', smarthr: '✓ built-in', rippling: '✗', deel: '✗', bamboohr: '✗' },
  { feature: 'Built-in e-sign (no DocuSign)', keenez: '✓', smarthr: '✓ for JP forms', rippling: '✓', deel: '✓', bamboohr: '~ partner' },
  { feature: 'Public API (REST)', keenez: '✓ at v1', smarthr: '~ limited', rippling: '✓ extensive', deel: '✓', bamboohr: '✓' },
  { feature: 'Free tier', keenez: '10 employees', smarthr: '30 (limited features)', rippling: '✗', deel: '✗', bamboohr: '✗' },
  { feature: 'Time to first employee onboarded', keenez: '< 10 min', smarthr: 'days', rippling: '90 sec demo / weeks real', deel: 'hours', bamboohr: '2–4 weeks' },
];

export function ComparePage() {
  const { t } = useTranslation();
  useDocumentMeta({
    title: 'Compare — KeenEZ vs SmartHR, Rippling, Deel, BambooHR',
    description: 'How KeenEZ stacks up against SmartHR, Rippling, Deel, and BambooHR on JP-native compliance, global payroll, AI agents, and public pricing.',
    path: '/compare',
  });
  return (
    <div className="min-h-screen">
      <SiteHeader />

      <main className="mx-auto max-w-6xl px-6 py-12 md:py-16">
        <div className="max-w-3xl">
          <p className="text-xs uppercase tracking-wide text-accent-600 font-medium mb-3">Compare</p>
          <h1 className="display text-4xl md:text-5xl font-semibold tracking-tight text-ink-900 serif-jp">
            {t('compare.title')}
          </h1>
          <p className="mt-3 text-ink-600">{t('compare.subtitle')}</p>
          <p className="mt-2 text-xs text-ink-500">
            Last updated 2026-05-23 · We update this when competitors change their products. Find an error? <a className="text-accent-600 underline" href="mailto:compare@keenez.com">Tell us.</a>
          </p>
        </div>

        <div className="mt-8 overflow-x-auto rounded-2xl border border-ink-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-ink-50 text-ink-700">
              <tr>
                <th className="text-start font-semibold px-4 py-3 sticky start-0 bg-ink-50 z-10">Feature</th>
                <th className="text-start font-semibold px-4 py-3 text-accent-700">KeenEZ</th>
                <th className="text-start font-semibold px-4 py-3">SmartHR</th>
                <th className="text-start font-semibold px-4 py-3">Rippling</th>
                <th className="text-start font-semibold px-4 py-3">Deel</th>
                <th className="text-start font-semibold px-4 py-3">BambooHR</th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map((r, i) => (
                <tr key={r.feature} className={i % 2 ? 'bg-white' : 'bg-ink-50/30'}>
                  <td className="px-4 py-3 font-medium text-ink-900 sticky start-0 bg-inherit">{r.feature}</td>
                  <td className="px-4 py-3 text-accent-700 font-semibold">{r.keenez}</td>
                  <td className="px-4 py-3 text-ink-700">{r.smarthr}</td>
                  <td className="px-4 py-3 text-ink-700">{r.rippling}</td>
                  <td className="px-4 py-3 text-ink-700">{r.deel}</td>
                  <td className="px-4 py-3 text-ink-700">{r.bamboohr}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-8 rounded-2xl border border-ink-200 bg-white p-6 md:p-8">
          <h3 className="text-lg font-semibold text-ink-900">Why teams choose KeenEZ</h3>
          <ul className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-ink-700">
            <li>· <span className="font-medium text-ink-900">One product, every market.</span> JP statutory rates + US/UK/DE engines + cross-border AI in a single workspace.</li>
            <li>· <span className="font-medium text-ink-900">Transparent pricing.</span> $0 → $8 → $19 → $39 per seat, published. No "talk to sales" gate.</li>
            <li>· <span className="font-medium text-ink-900">AI agents included.</span> Compliance Q&amp;A, Border Buddy, policy drafting, offer letters — every plan, no setup, no separate API key.</li>
            <li>· <span className="font-medium text-ink-900">Hanko-aware approvals.</span> JP-native sequential signer chains with 承認 / 却下 stamps, audit-defensible signature hashing.</li>
            <li>· <span className="font-medium text-ink-900">Free up to 10 employees.</span> No free trial. Free forever for small teams.</li>
            <li>· <span className="font-medium text-ink-900">Public REST API.</span> Workspace-scoped tokens, webhook subscriptions, HMAC-signed deliveries.</li>
          </ul>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
